import React, { useState } from 'react';
import { ShieldCheck, ExternalLink, KeyRound, Download } from 'lucide-react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { api } from '../../services/api';
import { WORDPRESS_PLUGIN_URL } from '../../config';

interface WooCommerceConnectProps {
  /** Prefilled when the merchant arrived from the WordPress plugin, which already knows its own address. */
  defaultStoreUrl?: string;
  /** Some hosts block WooCommerce's authorisation endpoint, so the old form stays reachable. */
  onUseManualSetup: () => void;
}

/**
 * Connect a store without anyone handling a credential.
 *
 * WooCommerce has an authorisation endpoint built for exactly this: the merchant approves on a
 * screen served by their OWN store, WooCommerce generates the key pair itself and posts it straight
 * to our backend. What the merchant does here is confirm an address and press a button.
 *
 * The alternative — the form this replaces — asks them to open WooCommerce, generate a REST key
 * pair, and copy two secrets across. That ends with live credentials in a browser tab, a clipboard
 * and usually an email, and it teaches merchants that pasting secrets into forms is normal.
 */
const WooCommerceConnect: React.FC<WooCommerceConnectProps> = ({
  defaultStoreUrl = '',
  onUseManualSetup,
}) => {
  const [storeUrl, setStoreUrl] = useState(defaultStoreUrl);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const handleConnect = async () => {
    const trimmed = storeUrl.trim();

    if (!trimmed) {
      setError('Enter your store address to continue.');
      return;
    }

    // Checked here as well as on the server, because this address is where every later credential
    // is sent — and a merchant who typed http deserves to be told before they approve anything.
    if (!trimmed.startsWith('https://')) {
      setError('The address must start with https:// — credentials are sent to it.');
      return;
    }

    setError(null);
    setIsStarting(true);

    try {
      const { authorizeUrl } = await api.startWooCommerceConnect(trimmed);
      // Their store, not ours. Leaving this tab is the flow, not a side effect of it.
      window.location.href = authorizeUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the connection.');
      setIsStarting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-4">
        <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900 dark:text-emerald-200">
          <p className="font-medium">You will not need to copy any keys.</p>
          <p className="mt-1 opacity-90">
            We send you to your own store's approval screen. WooCommerce creates the credentials and
            sends them to us directly — they are never shown to anyone.
          </p>
        </div>
      </div>

      <div>
        <Input
          label="Store address"
          id="woo-store-url"
          name="woo-store-url"
          type="url"
          placeholder="https://your-store.com"
          value={storeUrl}
          onChange={event => setStoreUrl(event.target.value)}
        />
        {error && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={handleConnect} isLoading={isStarting}>
          Continue to your store
          {!isStarting && <ExternalLink className="w-4 h-4 ml-2" />}
        </Button>

        <button
          type="button"
          onClick={onUseManualSetup}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline underline-offset-2"
        >
          <KeyRound className="w-3.5 h-3.5" />
          Enter API keys manually instead
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start gap-3">
          <Download className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              Install the WordPress plugin first
            </p>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              After you approve, WooCommerce returns you to your store's WordPress admin. The plugin
              is what picks the connection up there and finishes setting itself up — and it is what
              puts the assistant on your storefront without editing your theme.
            </p>
            <a
              href={WORDPRESS_PLUGIN_URL}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              Download the plugin
              <Download className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WooCommerceConnect;
